// Vedomost (payroll) — `xsib-hr` servisining `payroll/` qismi.
//
//   GET    /api/hr/payroll/runs?year=&month=&state=&page=&size=  hr.payroll.view
//   GET    /api/hr/payroll/runs/{id}                             hr.payroll.view
//   GET    /api/hr/payroll/runs/{id}/lines                       hr.payroll.view
//   POST   /api/hr/payroll/runs                                  hr.payroll.run
//   POST   /api/hr/payroll/runs/{id}/regenerate                  hr.payroll.run
//   POST   /api/hr/payroll/runs/{id}/approve                     hr.payroll.approve
//   POST   /api/hr/payroll/runs/{id}/paid                        hr.payroll.approve
//   POST   /api/hr/payroll/runs/{id}/cancel                      hr.payroll.approve
//   GET    /api/hr/payroll/persons/{id}/payslips?limit=          hr.payroll.view
//
// =====================================================================
// ⚠️ 1-QOIDA — O'Z VEDOMOSTINI TASDIQLASH: IKKI XIL JAVOB, IKKALASI HAM
//               PANELDA KO'RINISHI KERAK
// =====================================================================
// Backend qoidasi MOSLASHUVCHAN (`PayrollServiceImpl#requireSoleApprover`,
// egasi qarori 2026-08-13):
//
//   tashkilotda `hr.payroll.approve` li FAOL odam > 1
//        → 409, matn: «Vedomostni siz yig'gansiz, ya'ni uni boshqa
//          xodim tasdiqlashi kerak...»
//   yagona odam
//        → RUXSAT, lekin audit izida `selfApproved: true` va server
//          jurnalida WARN
//
// ⚠️ IKKINCHI HOLAT «muvaffaqiyat» EMAS. Server `OperationResult.OK`
// qaytaradi va javobda `selfApproved` maydoni YO'Q — u faqat AUDITGA
// yoziladi. Ya'ni panel buni javobdan bila olmaydi va o'zi hisoblashi
// SHART: {@link isSelfApproval}. Aks holda operator bir odam sifatida
// vedomostni yig'ib, tasdiqlab, hech qanday farq sezmasdi — holbuki bu
// amal audit jurnalida ALOHIDA belgilangan.
//
// ⚠️ Uchinchi holat ham bor va u ham 409: `identity` javob bermasa
// tekshiruv YOPIQ ishlaydi («men yagonamanmi» savoliga javob yo'q →
// rad etiladi). Bu ODATIY oqimni to'smaydi — boshqa odam tasdiqlasa
// `identity` ga chaqiruv umuman bo'lmaydi.
//
// =====================================================================
// ⚠️ 2-QOIDA — SUMMA HAR DOIM MUSBAT, ISHORA TURDAN KELADI
// =====================================================================
// `deductionMinor` javobda MUSBAT keladi (`PayrollRunView` javadoc'i) —
// `finance` ning ishora kelishuvidan ATAYLAB farq qiladi. Panelda uni
// manfiy qilib ko'rsatish yoki jamga qo'shib yuborish jimgina noto'g'ri
// son berardi. Shuning uchun `netMinor` HAM serverdan olinadi, panelda
// qayta hisoblanmaydi (bazada u `GENERATED ALWAYS` ustun).
//
// =====================================================================
// ⚠️ 3-QOIDA — DOIRA: `PAYROLL_RUN` DA `OWN` YO'Q va bu ATAYLAB
// =====================================================================
// `HrScope.PAYROLL_RUN` faqat `SUBTREE` ni qo'llab-quvvatlaydi
// (`.owner(...)` chaqiruvi UMUMAN yo'q). Sabab: vedomost — FILIALning
// hujjati, xodimniki emas; `OWN` doirali xodimga uni ochish butun
// filialning maosh jamini ko'rsatardi.
//
// ⚠️ Xodimning O'Z varaqasi esa boshqa yo'ldan keladi:
// `GET /payroll/persons/{id}/payslips` `HrScope.PERSON` ni ishlatadi
// (u yerda `OWN` BOR) va faqat `APPROVED`/`PAID` vedomostlarni beradi.
// Ya'ni bu ikki endpointni «bir xil ma'lumot» deb birlashtirmang.
//
// ⚠️ Doiradan tashqaridagi vedomost 404 qaytaradi, 403 EMAS
// (`HrNotFoundException` javadoc'i): 403 «bunday vedomost BOR» degan
// ma'lumotni oshkor qilardi.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { operationResultSchema, type OperationResult } from '@/shared/api/operationResult'
import { instantSchema, uuidSchema } from '@/shared/api/schemas'
import { money, type Money } from '@/shared/lib/money'
import type { OrgNode, OrgNodeType } from '@/features/org/orgApi'
import type { PageResult } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'
import { PAY_COMPONENT_KINDS, PAY_ORIGINS } from './payApi'

/**
 * Vedomost holati — `PayrollRunState` enumining nusxasi.
 *
 * O'tishlar: `DRAFT → APPROVED → PAID`, `DRAFT → CANCELLED`,
 * `APPROVED → CANCELLED`. Orqaga qaytish YO'Q va uni baza majburlaydi
 * (`hr.payroll_run_state_guard()`), ya'ni panel «tuzataman» deb
 * yozilgan tugma chizmaydi.
 */
export const PAYROLL_RUN_STATES = ['DRAFT', 'APPROVED', 'PAID', 'CANCELLED'] as const
export type PayrollRunState = (typeof PAYROLL_RUN_STATES)[number]

// --- Vedomost ----------------------------------------------------------------

const payrollRunSchema = z.object({
  id: uuidSchema,
  orgNodeId: uuidSchema,
  orgNodeCode: z.string().nullish(),
  orgNodeName: z.string().nullish(),
  periodYear: z.number(),
  periodMonth: z.number(),
  currency: z.string(),
  state: z.enum(PAYROLL_RUN_STATES),
  lineCount: z.number(),
  /** ⚠️ MUSBAT */
  earningMinor: z.number(),
  /** ⚠️ MUSBAT — manfiy EMAS */
  deductionMinor: z.number(),
  netMinor: z.number(),
  note: z.string().nullish(),
  /** ⚠️ FOYDALANUVCHI NOMI (`auth.getName()`), UUID emas — {@link isSelfApproval} */
  generatedBy: z.string().nullish(),
  generatedAt: instantSchema.nullish(),
  approvedBy: z.string().nullish(),
  approvedAt: instantSchema.nullish(),
  paidBy: z.string().nullish(),
  paidAt: instantSchema.nullish(),
})

export interface PayrollRun {
  id: string
  orgNodeId: string
  orgNodeCode: string | null
  orgNodeName: string | null
  periodYear: number
  periodMonth: number
  state: PayrollRunState
  lineCount: number
  earning: Money
  deduction: Money
  net: Money
  note: string | null
  generatedBy: string | null
  generatedAt: string | null
  approvedBy: string | null
  approvedAt: string | null
  paidBy: string | null
  paidAt: string | null
}

function toPayrollRun(raw: z.infer<typeof payrollRunSchema>): PayrollRun {
  return {
    id: raw.id,
    orgNodeId: raw.orgNodeId,
    orgNodeCode: raw.orgNodeCode ?? null,
    orgNodeName: raw.orgNodeName ?? null,
    periodYear: raw.periodYear,
    periodMonth: raw.periodMonth,
    state: raw.state,
    lineCount: raw.lineCount,
    earning: money(raw.earningMinor, raw.currency),
    deduction: money(raw.deductionMinor, raw.currency),
    net: money(raw.netMinor, raw.currency),
    note: raw.note ?? null,
    generatedBy: raw.generatedBy ?? null,
    generatedAt: raw.generatedAt ?? null,
    approvedBy: raw.approvedBy ?? null,
    approvedAt: raw.approvedAt ?? null,
    paidBy: raw.paidBy ?? null,
    paidAt: raw.paidAt ?? null,
  }
}

export function parsePayrollRun(raw: unknown): PayrollRun {
  return toPayrollRun(payrollRunSchema.parse(raw))
}

const payrollRunPageSchema = z.object({
  rows: z.array(payrollRunSchema),
  total: z.number(),
  page: z.number(),
  size: z.number(),
})

export function parsePayrollRunPage(raw: unknown): PageResult<PayrollRun> {
  const parsed = payrollRunPageSchema.parse(raw)
  return { items: parsed.rows.map(toPayrollRun), total: parsed.total }
}

export interface PayrollRunFilter {
  year: number | null
  month: number | null
  /**
   * ⚠️ Server bu qiymatni XOM holda SQL ga uzatadi va tanimasa
   * BO'SH sahifa qaytaradi, 400 EMAS (`PayrollRepository`). Shuning
   * uchun panel faqat {@link PAYROLL_RUN_STATES} dagi qiymatni
   * yuboradi — aks holda operator «vedomost yo'qoldi» degan holatga
   * tushardi.
   */
  state: PayrollRunState | null
}

export function fetchPayrollRunPage(
  filter: PayrollRunFilter,
): (state: TableState, signal: AbortSignal) => Promise<PageResult<PayrollRun>> {
  return async (state, signal) => {
    const response = await api.get<unknown>('/api/hr/payroll/runs', {
      signal,
      params: {
        ...(filter.year === null ? {} : { year: filter.year }),
        ...(filter.month === null ? {} : { month: filter.month }),
        ...(filter.state === null ? {} : { state: filter.state }),
        // Backend sahifani 0 dan sanaydi — `/api/hr/persons` bilan bir xil
        page: state.page - 1,
        size: state.pageSize,
      },
    })
    return parsePayrollRunPage(response.data)
  }
}

export async function fetchPayrollRun(id: string): Promise<PayrollRun> {
  const response = await api.get<unknown>(`/api/hr/payroll/runs/${id}`)
  return parsePayrollRun(response.data)
}

export interface PayrollRunDraft {
  /** ⚠️ Turi `is_cost_center` bo'lgan tugun — buni baza majburlaydi */
  orgNodeId: string
  year: number
  month: number
  note: string | null
}

export async function generatePayrollRun(draft: PayrollRunDraft): Promise<PayrollRun> {
  const response = await api.post<unknown>('/api/hr/payroll/runs', draft)
  return parsePayrollRun(response.data)
}

/** ⚠️ FAQAT `DRAFT` — boshqa holatda 409 */
export async function regeneratePayrollRun(id: string): Promise<PayrollRun> {
  const response = await api.post<unknown>(`/api/hr/payroll/runs/${id}/regenerate`)
  return parsePayrollRun(response.data)
}

export async function approvePayrollRun(id: string): Promise<OperationResult> {
  const response = await api.post<unknown>(`/api/hr/payroll/runs/${id}/approve`)
  return operationResultSchema.parse(response.data)
}

export async function markPayrollPaid(id: string): Promise<OperationResult> {
  const response = await api.post<unknown>(`/api/hr/payroll/runs/${id}/paid`)
  return operationResultSchema.parse(response.data)
}

export async function cancelPayrollRun(id: string): Promise<OperationResult> {
  const response = await api.post<unknown>(`/api/hr/payroll/runs/${id}/cancel`)
  return operationResultSchema.parse(response.data)
}

// --- Vedomost satrlari -------------------------------------------------------

const payrollLineItemSchema = z.object({
  id: uuidSchema,
  componentId: uuidSchema,
  /** ⚠️ MUZLATILGAN nusxa — katalogdagi nom keyin o'zgarsa ham bu qoladi */
  componentCode: z.string().nullish(),
  kind: z.enum(PAY_COMPONENT_KINDS),
  /** ⚠️ Enum EMAS, oddiy satr: `PayrollSource.ORIGIN_*` */
  origin: z.string(),
  amountMinor: z.number(),
  note: z.string().nullish(),
})

export interface PayrollLineItem {
  id: string
  componentId: string
  componentCode: string | null
  kind: (typeof PAY_COMPONENT_KINDS)[number]
  origin: string
  amountMinor: number
  note: string | null
}

const payrollLineSchema = z.object({
  id: uuidSchema,
  personId: uuidSchema,
  personnelNo: z.string().nullish(),
  fullName: z.string().nullish(),
  /** ⚠️ Yig'ilgan PAYTDAGI nusxa — xodim keyin ko'chsa ham o'zgarmaydi */
  orgNodeId: uuidSchema.nullish(),
  orgNodeName: z.string().nullish(),
  jobTitleId: uuidSchema.nullish(),
  jobTitleName: z.string().nullish(),
  earningMinor: z.number(),
  deductionMinor: z.number(),
  netMinor: z.number(),
  currency: z.string(),
  note: z.string().nullish(),
  items: z.array(payrollLineItemSchema),
})

export interface PayrollLine {
  id: string
  personId: string
  personnelNo: string | null
  fullName: string | null
  orgNodeId: string | null
  orgNodeName: string | null
  jobTitleId: string | null
  jobTitleName: string | null
  earning: Money
  deduction: Money
  net: Money
  note: string | null
  items: readonly PayrollLineItem[]
}

function toPayrollLine(raw: z.infer<typeof payrollLineSchema>): PayrollLine {
  return {
    id: raw.id,
    personId: raw.personId,
    personnelNo: raw.personnelNo ?? null,
    fullName: raw.fullName ?? null,
    orgNodeId: raw.orgNodeId ?? null,
    orgNodeName: raw.orgNodeName ?? null,
    jobTitleId: raw.jobTitleId ?? null,
    jobTitleName: raw.jobTitleName ?? null,
    earning: money(raw.earningMinor, raw.currency),
    deduction: money(raw.deductionMinor, raw.currency),
    net: money(raw.netMinor, raw.currency),
    note: raw.note ?? null,
    items: raw.items.map((item) => ({
      id: item.id,
      componentId: item.componentId,
      componentCode: item.componentCode ?? null,
      kind: item.kind,
      origin: item.origin,
      amountMinor: item.amountMinor,
      note: item.note ?? null,
    })),
  }
}

export function parsePayrollLines(raw: unknown): readonly PayrollLine[] {
  return z.array(payrollLineSchema).parse(raw).map(toPayrollLine)
}

export async function fetchPayrollLines(runId: string): Promise<readonly PayrollLine[]> {
  const response = await api.get<unknown>(`/api/hr/payroll/runs/${runId}/lines`)
  return parsePayrollLines(response.data)
}

/**
 * Xodimning hisob-kitob varaqalari.
 *
 * ⚠️ Faqat `APPROVED` va `PAID` vedomostlardan (`PayrollRepository`).
 * LOYIHA holatidagi son hali o'zgarishi mumkin va uni xodimga
 * ko'rsatish «maoshim kamaydi» degan savolni yaratardi.
 */
export async function fetchPayslips(
  personId: string,
  limit = 24,
): Promise<readonly PayrollLine[]> {
  const response = await api.get<unknown>(`/api/hr/payroll/persons/${personId}/payslips`, {
    params: { limit },
  })
  return parsePayrollLines(response.data)
}

// --- Sof mantiq --------------------------------------------------------------

/**
 * Vedomostni tasdiqlayotgan odam uni O'ZI yig'ganmi.
 *
 * ⚠️ SOLISHTIRISH FOYDALANUVCHI NOMI BO'YICHA, id bo'yicha EMAS.
 * `payroll_run.generated_by` ga `auth.getName()` yoziladi, u esa
 * `XsibAuthentication#getName` da `user.username()` (o'lchangan). Bu
 * yerga `user.id` ni qo'yish tekshiruvni HAR DOIM `false` qilardi va
 * ogohlantirish hech qachon chiqmasdi — jimgina.
 *
 * ⚠️ Bo'sh qiymatda `false`: server ham aynan shunday qaraydi
 * (`isSelfApproval` — ikkala tomon ham `null` bo'lmasligi shart).
 */
export function isSelfApproval(run: PayrollRun, username: string | null | undefined): boolean {
  if (!username || !run.generatedBy) return false
  return run.generatedBy === username
}

/** Tasdiqlash mumkinmi — FAQAT loyiha holati (`requireDraft`) */
export function canApprove(run: PayrollRun): boolean {
  return run.state === 'DRAFT'
}

/** «To'landi» deb belgilash mumkinmi — FAQAT tasdiqlangan */
export function canMarkPaid(run: PayrollRun): boolean {
  return run.state === 'APPROVED'
}

/**
 * Bekor qilish mumkinmi.
 *
 * ⚠️ `PAID` dan bekor qilish YO'Q — pul chiqib bo'lgan. `CANCELLED`
 * dan ham yo'q. Ikkalasini ham baza majburlaydi, bu yerdagisi faqat
 * tugmani ochmaslik uchun.
 */
export function canCancel(run: PayrollRun): boolean {
  return run.state === 'DRAFT' || run.state === 'APPROVED'
}

/** Qayta yig'ish mumkinmi — FAQAT loyiha */
export function canRegenerate(run: PayrollRun): boolean {
  return run.state === 'DRAFT'
}

/** `2026-08` — davrni bitta o'qiladigan yorliqqa keltiradi */
export function periodLabel(run: Pick<PayrollRun, 'periodYear' | 'periodMonth'>): string {
  return `${String(run.periodYear)}-${String(run.periodMonth).padStart(2, '0')}`
}

/** Vedomost satridagi qism qayerdan kelgan — noma'lum qiymat XOM ko'rsatilmaydi */
export function isKnownOrigin(origin: string): boolean {
  return (PAY_ORIGINS as readonly string[]).includes(origin)
}

/**
 * Vedomost yig'ish MUMKIN bo'lgan tugunlar — §19.3.
 *
 * ⚠️ Qaror turning NOMIGA emas, `costCenter` IMKONIYATIGA qarab
 * chiqariladi — `employeeNodes` bilan aynan bir xil naqsh, faqat
 * boshqa bayroq. Administrator «Uchastka» degan yangi tur ochib
 * `is_cost_center = true` qo'ysa, ro'yxat darhol uni ham ko'rsatadi.
 *
 * ⚠️ Turi ro'yxatda TOPILMAGAN tugun CHIQARIB TASHLANADI: bu ro'yxat
 * YOZISH uchun va noto'g'ri tanlovni server rad etardi (404 «Tashkiliy
 * tugun topilmadi» — doiradan tashqarilik bilan bir xil xabar, ya'ni
 * operator sababni topa olmasdi).
 *
 * ⚠️ `CLOSED` tugun ham chiqariladi: yopilgan filialga yangi vedomost
 * yig'ilmaydi. `SUSPENDED` QOLADI — u vaqtinchalik holat va o'sha oyda
 * xodimlar ishlagan bo'lishi mumkin.
 */
export function costCenterNodes(
  nodes: readonly OrgNode[],
  types: readonly OrgNodeType[],
): readonly OrgNode[] {
  const centers = new Set(types.filter((type) => type.costCenter).map((type) => type.code))
  return nodes.filter((node) => node.state !== 'CLOSED' && centers.has(node.kind))
}
